import React, { useContext, useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import Style from "./style.module.css";
import moment from "moment";
import { Trash2 } from "lucide-react";
import Select from "react-select";

import {
  FeildError,
  FeildWraper,
  FormArea,
  FormLabel,
} from "../../../component/form/form-layout";
import LeftSidePopupLayout from "../../../component/popup/modal-layout";

import { GlobalContext } from "../../../context/Context";
import XCloseIcon from "../../../component/icons/x-close";
import { useApiCall } from "../../../component/custom-hooks/api-call";

const stripHtml = (html) => (html || "").replace(/<[^>]*>?/gm, "");

const CustomSelect = ({
  name,
  options,
  formik,
  placeholder,
  handleChangeExtra,
}) => {
  return (
    <Select
      name={name}
      className="text-sm"
      classNamePrefix="react-select"
      options={options}
      components={{
        IndicatorSeparator: () => null,
        LoadingIndicator: () => null,
      }}
      value={options?.find((opt) => opt?.value === formik.values[name])}
      onChange={(selectedOption) => {
        formik.setFieldValue(name, selectedOption?.value || "");
        if (handleChangeExtra) handleChangeExtra(selectedOption);
      }}
      filterOption={(option, inputValue) =>
        option?.label?.toLowerCase().includes(inputValue.toLowerCase())
      }
      placeholder={placeholder}
      isClearable
      menuPlacement="auto"
      styles={{
        control: (base, state) => ({
          ...base,
          minHeight: "35px",
          height: "auto",
          borderRadius: "5px",
          border: "1px solid #DFDFDF",
          backgroundColor: state.isDisabled ? "#f0f0f0" : "#FFF",
          boxShadow: state.isFocused ? "none" : "none",
          "&:hover": { borderColor: "#DFDFDF" },
          fontSize: "13px",
          lineHeight: "1.2",
          fontWeight: 500,
          color: "#1E1E1E",
          padding: "0 14px",
          cursor: state.isDisabled ? "not-allowed" : "normal",
        }),
        valueContainer: (base) => ({
          ...base,
          height: "auto",
          padding: "0 0px 0",
        }),
        input: (base) => ({
          ...base,
          margin: 0,
          padding: 0,
          fontSize: "14px",
          height: "auto",
        }),
        singleValue: (base) => ({
          ...base,
          fontSize: "14px",
          lineHeight: "1.2",
          fontWeight: 500,
        }),
        placeholder: (base) => ({
          ...base,
          fontSize: "14px",
          color: "#717680",
        }),
        menuPortal: (base) => ({ ...base, zIndex: 99999 }),
        menu: (base) => ({
          ...base,
          marginTop: "2px",
          borderRadius: "5px",
          border: "1px solid #DFDFDF",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }),
        option: (base, state) => ({
          ...base,
          fontSize: "14px",
          lineHeight: "1.2",
          fontWeight: 500,
          color: state.isSelected ? "#FFF" : "#1E1E1E",
          backgroundColor: state.isSelected
            ? "#3b82f6"
            : state.isFocused
              ? "#F3F4F6"
              : "#FFF",
          padding: "8px 14px",
          cursor: "pointer",
        }),
        indicatorContainer: (base) => ({ ...base, padding: 0 }),
        dropdownIndicator: (base) => ({ ...base, padding: 0 }),
      }}
    />
  );
};

const RefundForm = ({
  onSubmit = () => { },
  onClose = () => { },
  onError = () => { },
  refundDataId = {},
}) => {
  let { state, dispatch } = useContext(GlobalContext);
  // console.log("refundDataId", refundDataId)

  const editid = refundDataId?.id;
  const [refundData, setRefundData] = useState([]);

  const apiCall = useApiCall();

  const getEditFeilds = async () => {
    try {
      const res = await apiCall({
        method: "get",
        url: `refund-trackers/edit-refund/${editid}`,
      });

      setRefundData(res?.data || []);
    } catch (error) {
      console.warn("Error fetching refund data:", error);
    }
  };

  // Generate 10-year range dynamically
  //   const currentYear = new Date().getFullYear();
  //   const yearOptions = Array.from({ length: 10 }, (_, i) => {
  //     const year = currentYear - 4 + i; // 4 years before, 5 years after
  //     return { value: year.toString(), label: year.toString() };
  //   });

  useEffect(() => {
    getBrands();
    getBrandProducts();
    getMerchants();
    getMerchantType();
    getAgents();
    getDropdowns();
    getEditFeilds();
  }, []);

  useEffect(() => {
    if (!refundData?.id) return;

    const values = { ...formik.values };

    const fields = [
      "ref_no",
      "order_id",
      "client_name",
      "email",
      "brand_website",
      "merchant_name",
      "processor",
      "product_service",
      "order_date",
      "amount_paid",
      "work_status",
      "project_percent_done",
      "cb_or_refund",
      "request_date",
      "process_date",
      "refund_amount",
      "dispute_amount",
      "source",
      "front_sale",
      "account_manager",
      "processor_reason_code",
      "qa_status",
      "identified_by_qa",
      "responsible",
      "responsible_person",
      "qa_executive",
      "qa_analysis",
      "disagreement_with_qa",
      "qa_decision_changed",
      "qa_of_qa",
      "first_call_upsell_attempt",
      "repitch_attempted",
      "repitch_outcome",
      "cb_outcome",
      "recovery_rate_percent",
      "case_study",
    ];

    const numberFields = [
      "brand_website",
      "merchant_name",
      "processor",
      "product_service",
      "work_status",
      "cb_or_refund",
      "source",
      "qa_status",
      "account_manager",
      "front_sale",
      "qa_executive",
      "repitch_outcome",
      "cb_outcome",
      "responsible",
      "responsible_person"
    ];

    fields.forEach((field) => {
      let value = refundData?.[field] ?? "";

      if (numberFields.includes(field)) {
        value = value !== null && value !== "" ? Number(value) : "";
      }

      values[field] = value;
    });

    values.business_unit =
      refundData?.agent_heads?.business_unit ?? "";
    values.reporting_authority =
      refundData?.agent_heads?.reporting_authority ?? "";
    values.team_lead_1 =
      refundData?.agent_heads?.team_lead_1 ?? "";
    values.team_lead_2 =
      refundData?.agent_heads?.team_lead_2 ?? "";

    formik.setValues(values);

  }, [refundData]);


  const [brands, setBrands] = useState([]);


  const [agents, setAgents] = useState([]);
  const [brandsProducts, setBrandsProducts] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [merchantsType, setMerchantsType] = useState([]);

  //
  const [cbOrRefundOptions, setCbOrRefundOptions] = useState([]);
  const [cbOutcomeOptions, setCbOutcomeOptions] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [qaStatusOptions, setQaStatusOptions] = useState([]);
  const [quarterOptions, setQuarterOptions] = useState([]);
  const [repitchOutcomeOptions, setRepitchOutcomeOptions] = useState([]);
  const [responsibleOptions, setResponsibleOptions] = useState([]);
  const [sourceOptions, setSourceOptions] = useState([]);
  const [workStatusOptions, setWorkStatusOptions] = useState([]);
  const [yearOptions, setYearOptions] = useState([]);
  const [processorReasonOptions, setProcessorReasonOptions] = useState([]);

  const getBrands = async () => {
    try {
      const res = await apiCall({
        method: "get",
        url: "brands/brands-list",
      });

      setBrands(res?.data || []);
    } catch (error) {
      console.warn("Error fetching brands:", error);
    }
  };



  const getBrandProducts = async () => {
    try {
      const res = await apiCall({
        method: "get",
        url: "brands/brand-products-list?is_paginate=0",
      });

      setBrandsProducts(res?.data || []);
    } catch (error) {
      console.warn("Error fetching brands:", error);
    }
  };

  const getMerchants = async () => {
    try {
      const res = await apiCall({
        method: "get",
        url: "merchants/merchants-list",
      });

      setMerchants(res?.data || []);
    } catch (error) {
      console.warn("Error fetching brands:", error);
    }
  };

  const getMerchantType = async () => {
    try {
      const res = await apiCall({
        method: "get",
        url: "merchants/merchants-types-list-settings",
      });

      setMerchantsType(res?.data || []);
    } catch (error) {
      console.warn("Error fetching brands:", error);
    }
  };

  const getAgents = async () => {
    try {
      const res = await apiCall({
        method: "get",
        url: "users/agents-list",
      });

      setAgents(res?.agents || []);
    } catch (error) {
      console.warn("Error fetching brands:", error);
    }
  };

  const mapDropdownObjectWithId = (obj = {}) => {
    return Object.entries(obj).map(([key, value]) => ({
      label: value.toString(),
      value: Number(key), // 👈 ID as value
    }));
  };

  const getDropdowns = async () => {
    try {
      const res = await apiCall({
        method: "get",
        url: "get-dropdowns",
      });

      const data = res?.data || {};

      setCbOrRefundOptions(mapDropdownObjectWithId(data.cb_or_refund));
      setCbOutcomeOptions(mapDropdownObjectWithId(data.cb_outcome));
      setDepartmentOptions(mapDropdownObjectWithId(data.departments));
      setQaStatusOptions(mapDropdownObjectWithId(data.qa_status));
      setQuarterOptions(mapDropdownObjectWithId(data.quarter));
      setRepitchOutcomeOptions(mapDropdownObjectWithId(data.repitch_outcome));
      setResponsibleOptions(mapDropdownObjectWithId(data.responsible));
      setSourceOptions(mapDropdownObjectWithId(data.source));
      setWorkStatusOptions(mapDropdownObjectWithId(data.work_status));
      setProcessorReasonOptions(mapDropdownObjectWithId(data?.work_status));

      // year (already numeric)
      setYearOptions(
        Object.entries(data.year || {}).map(([key, value]) => ({
          label: value.toString(),
          value: Number(key),
        }))
      );
    } catch (error) {
      console.warn("Error fetching dropdowns:", error);
    }
  };

  const YesNo = [
    {
      key: "Yes",
      value: "Yes",
    },
    {
      key: "No",
      value: "No",
    },
  ];

  const months = [
    { key: "January", value: "January" },
    { key: "February", value: "February" },
    { key: "March", value: "March" },
    { key: "April", value: "April" },
    { key: "May", value: "May" },
    { key: "June", value: "June" },
    { key: "July", value: "July" },
    { key: "August", value: "August" },
    { key: "September", value: "September" },
    { key: "October", value: "October" },
    { key: "November", value: "November" },
    { key: "December", value: "December" },
  ];

  const percentage = [
    { key: "10", value: "10" },
    { key: "20", value: "20" },
    { key: "30", value: "30" },
    { key: "40", value: "40" },
    { key: "50", value: "50" },
    { key: "60", value: "60" },
    { key: "70", value: "70" },
    { key: "80", value: "80" },
    { key: "90", value: "90" },
    { key: "100", value: "100" },
  ];

  const brandsOptions = brands?.map((each) => ({
    value: each.brandsid,
    label: each.brandname,
  }));
  const brandsProductsOptions = brandsProducts?.map((each) => ({
    value: each.id,
    label: each.products,
  }));
  const merchantsOptions = merchants?.map((each) => ({
    value: each.id,
    label: each.company_name,
  }));
  const merchantsTypeOptions = merchantsType?.map((each) => ({
    value: each.id,
    label: each.type_name,
  }));

  const agentOptions = agents?.map((each) => ({
    value: each.userid,
    label: each.name,
  }));

  const yesNoOptions = YesNo?.map((each) => ({
    value: each.key,
    label: each.value,
  }));

  const monthOptions = months?.map((each) => ({
    value: each.key,
    label: each.value,
  }));

  const percentageOptions = percentage?.map((each) => ({
    value: each.key,
    label: each.value,
  }));

  const validationSchema = yup.object({
    // ref_no: yup
    //   .string("This Field is Required")
    //   .required("This Field is Required"),
    // order_id: yup
    //   .string("This Field is Required")
    //   .required("This Field is Required"),
    // client_name: yup
    //   .string("This Field is Required")
    //   .required("This Field is Required"),
    // work_status: yup
    //   .string("This Field is Required")
    //   .required("This Field is Required"),
    // email: yup
    //   .string("This Field is Required")
    //   .required("This Field is Required"),
    // brand_website: yup
    //   .string("This Field is Required")
    //   .required("This Field is Required"),
    // merchant_name: yup
    //   .string("This Field is Required")
    //   .required("This Field is Required"),
    // processor: yup
    //   .string("This Field is Required")
    //   .required("This Field is Required"),
    // product_service: yup
    //   .string("This Field is Required")
    //   .required("This Field is Required"),
    // order_date: yup
    //   .string("This Field is Required")
    //   .required("This Field is Required"),
    // amount_paid: yup
    //   .string("This Field is Required")
    //   .required("This Field is Required"),
    // project_percent_done: yup
    //   .string("This Field is Required")
    //   .required("This Field is Required"),
    // cb_or_refund: yup
    //   .string("This Field is Required")
    //   .required("This Field is Required"),
    // request_date: yup
    //   .string("This Field is Required")
    //   .required("This Field is Required"),
    // process_date: yup
    //   .string("This Field is Required")
    //   .required("This Field is Required"),
    // refund_amount: yup
    //   .string("This Field is Required")
    //   .required("This Field is Required"),
    // dispute_amount: yup
    //   .string("This Field is Required")
    //   .required("This Field is Required"),
    // source: yup
    //   .string("This Field is Required")
    //   .required("This Field is Required"),
    // front_sale: yup
    //   .string("This Field is Required")
    //   .required("This Field is Required"),
    // account_manager: yup
    //   .string("This Field is Required")
    //   .required("This Field is Required"),
    // processor_reason_code: yup
    //   .string("This Field is Required")
    //   .required("This Field is Required"),
    // qa_status: yup
    //   .string("This Field is Required")
    //   .required("This Field is Required"),
    // identified_by_qa: yup
    //   .string("This Field is Required")
    //   .required("This Field is Required"),
    // responsible: yup
    //   .string("This Field is Required")
    //   .required("This Field is Required"),
    // business_unit: yup
    //   .string("This Field is Required")
    //   .required("This Field is Required"),
    // reporting_authority: yup
    //   .string("This Field is Required")
    //   .required("This Field is Required"),
    // team_lead_1: yup
    //   .string("This Field is Required")
    //   .required("This Field is Required"),
    // team_lead_2: yup
    //   .string("This Field is Required")
    //   .required("This Field is Required"),
    // responsible_person: yup
    //   .string("This Field is Required")
    //   .required("This Field is Required"),
    // qa_executive: yup
    //   .string("This Field is Required")
    //   .required("This Field is Required"),
    // qa_analysis: yup
    //   .string("This Field is Required")
    //   .required("This Field is Required"),
    // disagreement_with_qa: yup
    //   .string("This Field is Required")
    //   .required("This Field is Required"),
    // qa_decision_changed: yup
    //   .string("This Field is Required")
    //   .required("This Field is Required"),
    // qa_of_qa: yup
    //   .string("This Field is Required")
    //   .required("This Field is Required"),
    // first_call_upsell_attempt: yup
    //   .string("This Field is Required")
    //   .required("This Field is Required"),
    // repitch_attempted: yup
    //   .string("This Field is Required")
    //   .required("This Field is Required"),
    // repitch_outcome: yup
    //   .string("This Field is Required")
    //   .required("This Field is Required"),
    // cb_outcome: yup
    //   .string("This Field is Required")
    //   .required("This Field is Required"),
    // net_loss: yup
    //   .string("This Field is Required")
    //   .required("This Field is Required"),
    // recovery_rate_percent: yup
    //   .string("This Field is Required")
    //   .required("This Field is Required"),
    // case_study: yup
    //   .string("This Field is Required")
    //   .required("This Field is Required"),
  });

  const formik = useFormik({
    initialValues: {
      ref_no: "",
      order_id: "",
      client_name: "",
      email: "",
      brand_website: "",
      merchant_name: "",
      processor: "",
      product_service: "",
      order_date: "",
      amount_paid: "",
      work_status: "",
      project_percent_done: "",
      cb_or_refund: "",
      request_date: "",
      process_date: "",
      refund_amount: "",
      dispute_amount: "",
      source: "",
      front_sale: "",
      account_manager: "",
      processor_reason_code: "",
      qa_status: "",
      identified_by_qa: "",
      responsible: "",
      business_unit: "",
      reporting_authority: "",
      team_lead_1: "",
      team_lead_2: "",
      responsible_person: "",
      qa_executive: "",
      qa_analysis: "",
      disagreement_with_qa: "",
      qa_decision_changed: "",
      qa_of_qa: "",
      first_call_upsell_attempt: "",
      repitch_attempted: "",
      repitch_outcome: "",
      cb_outcome: "",
      net_loss: "",
      recovery_rate_percent: "",
      case_study: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      console.log("Values", values);

      let extraField = {}

      if (values.responsible == "1") {
        extraField = {
          team_lead_1: values?.team_lead_1,
          team_lead_2: values?.team_lead_2,
          responsible_person: values?.responsible_person,
          reporting_authority: values?.reporting_authority,
        }
      }

      let data = {
        ...extraField,
        ref_no: values?.ref_no,
        order_id: values?.order_id,
        client_name: values?.client_name,
        email: values?.email,
        brand_website: values?.brand_website,
        merchant_name: values?.merchant_name,
        processor: values?.processor,
        product_service: values?.product_service,
        order_date: values?.order_date,
        amount_paid: Number(values?.amount_paid),
        work_status: values?.work_status,
        project_percent_done: values?.project_percent_done,
        cb_or_refund: values?.cb_or_refund,
        request_date: values?.request_date,
        process_date: values?.process_date,
        refund_amount: Number(values?.refund_amount),
        dispute_amount: Number(values?.dispute_amount),
        source: values?.source,
        front_sale: values?.front_sale,
        account_manager: values?.account_manager,
        processor_reason_code: values?.processor_reason_code,
        qa_status: values?.qa_status,
        identified_by_qa: values?.identified_by_qa,
        responsible: values?.responsible,
        business_unit: values?.business_unit,
        qa_executive: values?.qa_executive,
        qa_analysis: values?.qa_analysis,
        disagreement_with_qa: values?.disagreement_with_qa,
        qa_decision_changed: values?.qa_decision_changed,
        qa_of_qa: values?.qa_of_qa,
        first_call_upsell_attempt: values?.first_call_upsell_attempt,
        repitch_attempted: values?.repitch_attempted,
        repitch_outcome: values?.repitch_outcome,
        cb_outcome: values?.cb_outcome,
        recovery_rate_percent: Number(values?.recovery_rate_percent),
        case_study: values?.case_study,
      };

      try {
        let apiRes;

        apiRes = await apiCall({
          method: "post",
          url: refundData?.id ? `refund-trackers/update-refund/${refundData.id}` : `refund-trackers/add`,
          data: data,
        });

        formik.resetForm();
        onSubmit(apiRes?.message || `Request Submitted`);
      } catch (error) {
        console.log("Error", error);
        onError(
          error?.response.data?.respMsg ||
          error?.response.data?.responseMessage ||
          error?.response.data?.message ||
          "Some Thing Went Wrong"
        );
      }
    },
  });

  // const isSalesResponsible = String(formik.values.responsible) === "1";

  // console.log(formik.values.responsible);
  // console.log(isSalesResponsible)

  // useEffect(() => {
  //   if (!isSalesResponsible) {
  //     formik.setFieldValue("team_lead_1", "");
  //     formik.setFieldValue("team_lead_2", "");
  //     formik.setFieldValue("responsible_person", "");
  //   }
  // }, [isSalesResponsible]);

  return (
    <form onSubmit={formik.handleSubmit}>
      <LeftSidePopupLayout
        popupStatus={true}
        className={`${Style.addMeModal} ${Style.leaveModal} !gap-0`}
      >
        <div
          className={`${Style["modal_header"]} ${Style.addMeModal_head} !flex !justify-between !items-center`}
        >
          <h4>{refundData?.id ? "Update" : "Add"} Refund</h4>
          <div
            onClick={onClose}
            className={`${Style["close_btn"]} cursor-pointer hover:text-red-500 p-1 rounded hover:bg-gray-200 transition-all duration-300`}
          >
            <XCloseIcon />
          </div>
        </div>

        <div className={`${Style.addMeModal_body}`}>
          <div className={`${Style.dcmb__card}`}>
            <div className={`${Style.dcmb__cardBody}`}>
              <div className={`${Style.dcmb__cardBody_formArea} pt-3`}>
                <FormArea>
                  {/* 1. Ref No */}
                  <FeildWraper width="33%" className={Style.responsive33}>
                    <FormLabel>Ref No</FormLabel>
                    <input
                      type="text"
                      name="ref_no"
                      value={formik.values.ref_no}
                      onChange={formik.handleChange}
                      placeholder="Ref No"
                    />
                    {formik.touched.ref_no && formik.errors.ref_no && (
                      <FeildError>{formik.errors.ref_no}</FeildError>
                    )}
                  </FeildWraper>

                  {/* 2. Order ID */}
                  <FeildWraper width="33%" className={Style.responsive33}>
                    <FormLabel>Order ID</FormLabel>
                    <input
                      type="text"
                      name="order_id"
                      value={formik.values.order_id}
                      onChange={formik.handleChange}
                      placeholder="Order ID"
                    />
                    {formik.touched.order_id && formik.errors.order_id && (
                      <FeildError>{formik.errors.order_id}</FeildError>
                    )}
                  </FeildWraper>

                  {/* 3. Client Name */}
                  <FeildWraper width="33%" className={Style.responsive33}>
                    <FormLabel>Client Name</FormLabel>
                    <input
                      type="text"
                      name="client_name"
                      value={formik.values.client_name}
                      onChange={formik.handleChange}
                      placeholder="Client Name"
                    />
                    {formik.touched.client_name &&
                      formik.errors.client_name && (
                        <FeildError>{formik.errors.client_name}</FeildError>
                      )}
                  </FeildWraper>

                  {/* 4. Email */}
                  <FeildWraper width="33%" className={Style.responsive33}>
                    <FormLabel>Email</FormLabel>
                    <input
                      type="text"
                      name="email"
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      placeholder="Email"
                    />
                    {formik.touched.email && formik.errors.email && (
                      <FeildError>{formik.errors.email}</FeildError>
                    )}
                  </FeildWraper>

                  {/* 5. Brand */}
                  <FeildWraper width="33%" className={Style.responsive33}>
                    <FormLabel>Brand</FormLabel>
                    <CustomSelect
                      name="brand_website"
                      options={brandsOptions}
                      formik={formik}
                      placeholder="Search or select Brands..."
                    />
                    {formik.touched.brand_website &&
                      formik.errors.brand_website && (
                        <FeildError>{formik.errors.brand_website}</FeildError>
                      )}
                  </FeildWraper>

                  {/* 6. Merchant Name */}
                  <FeildWraper width="33%" className={Style.responsive33}>
                    <FormLabel>Merchant Name</FormLabel>
                    <CustomSelect
                      name="merchant_name"
                      options={merchantsOptions}
                      formik={formik}
                      placeholder="Search or select Merchant..."
                    />
                    {formik.touched.merchant_name &&
                      formik.errors.merchant_name && (
                        <FeildError>{formik.errors.merchant_name}</FeildError>
                      )}
                  </FeildWraper>

                  {/* 7. Processor */}
                  <FeildWraper width="33%" className={Style.responsive33}>
                    <FormLabel>Processor</FormLabel>
                    <CustomSelect
                      name="processor"
                      options={merchantsTypeOptions}
                      formik={formik}
                      placeholder="Search or select Processor..."
                    />
                    {formik.touched.processor && formik.errors.processor && (
                      <FeildError>{formik.errors.processor}</FeildError>
                    )}
                  </FeildWraper>

                  {/* 8. Product */}
                  <FeildWraper width="33%" className={Style.responsive33}>
                    <FormLabel>Product</FormLabel>
                    <CustomSelect
                      name="product_service"
                      options={brandsProductsOptions}
                      formik={formik}
                      placeholder="Search or select Product..."
                    />
                    {formik.touched.product_service &&
                      formik.errors.product_service && (
                        <FeildError>{formik.errors.product_service}</FeildError>
                      )}
                  </FeildWraper>

                  {/* 9. Order Date */}
                  <FeildWraper width="33%" className={Style.responsive33}>
                    <FormLabel>Order Date</FormLabel>
                    <input
                      type="date"
                      name="order_date"
                      max={new Date().toISOString().split("T")[0]}
                      value={formik.values.order_date}
                      onChange={formik.handleChange}
                      placeholder="Order Date"
                    />
                    {formik.touched.order_date && formik.errors.order_date && (
                      <FeildError>{formik.errors.order_date}</FeildError>
                    )}
                  </FeildWraper>

                  {/* 10. Amount Paid */}
                  <FeildWraper width="33%" className={Style.responsive33}>
                    <FormLabel>Amount Paid</FormLabel>
                    <input
                      type="number"
                      name="amount_paid"
                      min="0"
                      value={formik.values.amount_paid}
                      onChange={formik.handleChange}
                      placeholder="Amount Paid"
                    />
                    {formik.touched.amount_paid &&
                      formik.errors.amount_paid && (
                        <FeildError>{formik.errors.amount_paid}</FeildError>
                      )}
                  </FeildWraper>

                  {/* 11. Work Status */}
                  <FeildWraper width="33%" className={Style.responsive33}>
                    <FormLabel>Work Status</FormLabel>

                    <CustomSelect
                      name="work_status"
                      options={workStatusOptions}
                      formik={formik}
                      placeholder="Select Work Status"
                    />

                    {formik.touched.work_status &&
                      formik.errors.work_status && (
                        <FeildError>{formik.errors.work_status}</FeildError>
                      )}
                  </FeildWraper>

                  {/* 12. Project Done Percentage */}
                  <FeildWraper width="33%" className={Style.responsive33}>
                    <FormLabel>Project Done</FormLabel>
                    {/* <CustomSelect
                      name="project_percent_done"
                      options={percentageOptions}
                      formik={formik}
                      placeholder="Select Percentage"
                    /> */}
                    <input
                      type="text"
                      name="project_percent_done"
                      value={formik.values.project_percent_done}
                      onChange={formik.handleChange}
                      placeholder="Project Done"
                    />

                    {formik.touched.project_percent_done &&
                      formik.errors.project_percent_done && (
                        <FeildError>
                          {formik.errors.project_percent_done}
                        </FeildError>
                      )}
                  </FeildWraper>

                  {/* 13. CB or Refund */}
                  <FeildWraper width="33%" className={Style.responsive33}>
                    <FormLabel>CB or Refund</FormLabel>
                    <CustomSelect
                      name="cb_or_refund"
                      options={cbOrRefundOptions}
                      formik={formik}
                      placeholder="Select CB/Refund"
                    />
                    {formik.touched.cb_or_refund &&
                      formik.errors.cb_or_refund && (
                        <FeildError>{formik.errors.cb_or_refund}</FeildError>
                      )}
                  </FeildWraper>

                  {/* 14. Request Date */}
                  <FeildWraper width="33%" className={Style.responsive33}>
                    <FormLabel>Request Date</FormLabel>
                    <input
                      type="date"
                      name="request_date"
                      max={new Date().toISOString().split("T")[0]}
                      value={formik.values.request_date}
                      onChange={formik.handleChange}
                      placeholder="Request Date"
                    />
                    {formik.touched.request_date &&
                      formik.errors.request_date && (
                        <FeildError>{formik.errors.request_date}</FeildError>
                      )}
                  </FeildWraper>

                  {/* 15. Process Date */}
                  <FeildWraper width="33%" className={Style.responsive33}>
                    <FormLabel>Process Date</FormLabel>
                    <input
                      type="date"
                      name="process_date"
                      max={new Date().toISOString().split("T")[0]}
                      value={formik.values.process_date}
                      onChange={formik.handleChange}
                      placeholder="Process Date"
                    />
                    {formik.touched.process_date &&
                      formik.errors.process_date && (
                        <FeildError>{formik.errors.process_date}</FeildError>
                      )}
                  </FeildWraper>

                  {/* 16. Refund Amount */}
                  <FeildWraper width="33%" className={Style.responsive33}>
                    <FormLabel>Refund Amount</FormLabel>
                    <input
                      type="number"
                      name="refund_amount"
                      min="0"
                      value={formik.values.refund_amount}
                      onChange={formik.handleChange}
                      placeholder="Refund Amount"
                    />
                    {formik.touched.refund_amount &&
                      formik.errors.refund_amount && (
                        <FeildError>{formik.errors.refund_amount}</FeildError>
                      )}
                  </FeildWraper>

                  {/* 17. Dispute Amount */}
                  <FeildWraper width="33%" className={Style.responsive33}>
                    <FormLabel>Dispute Amount</FormLabel>
                    <input
                      type="number"
                      name="dispute_amount"
                      min="0"
                      value={formik.values.dispute_amount}
                      onChange={formik.handleChange}
                      placeholder="Dispute Amount"
                    />
                    {formik.touched.dispute_amount &&
                      formik.errors.dispute_amount && (
                        <FeildError>{formik.errors.dispute_amount}</FeildError>
                      )}
                  </FeildWraper>

                  {/* 20. Source */}
                  <FeildWraper width="33%" className={Style.responsive33}>
                    <FormLabel>Source</FormLabel>

                    <CustomSelect
                      name="source"
                      options={sourceOptions}
                      formik={formik}
                      placeholder="Select Source"
                    />

                    {formik.touched.source && formik.errors.source && (
                      <FeildError>{formik.errors.source}</FeildError>
                    )}
                  </FeildWraper>

                  {/* 21. Front Sale */}
                  <FeildWraper width="33%" className={Style.responsive33}>
                    <FormLabel>Front Sale</FormLabel>
                    <CustomSelect
                      name="front_sale"
                      options={agentOptions}
                      formik={formik}
                      placeholder="Select Front Sale"
                    />
                    {formik.touched.front_sale && formik.errors.front_sale && (
                      <FeildError>{formik.errors.front_sale}</FeildError>
                    )}
                  </FeildWraper>

                  {/* 22. Account Manager */}
                  <FeildWraper width="33%" className={Style.responsive33}>
                    <FormLabel>Account Manager</FormLabel>
                    <CustomSelect
                      name="account_manager"
                      options={agentOptions}
                      formik={formik}
                      placeholder="Select Account Manager"
                    />
                    {formik.touched.account_manager &&
                      formik.errors.account_manager && (
                        <FeildError>{formik.errors.account_manager}</FeildError>
                      )}
                  </FeildWraper>

                  {/* 23. Processor Reason Code */}
                  <FeildWraper width="33%" className={Style.responsive33}>
                    <FormLabel>Processor Reason Code</FormLabel>
                    <input
                      type="text"
                      name="processor_reason_code"
                      value={formik.values.processor_reason_code}
                      onChange={formik.handleChange}
                      placeholder="Processor Reason Code"
                    />
                    {/* <CustomSelect
                      name="processor_reason_code"
                      options={processorReasonOptions}
                      formik={formik}
                      placeholder="Select Processor Reason Code"
                    /> */}
                    {formik.touched.processor_reason_code &&
                      formik.errors.processor_reason_code && (
                        <FeildError>
                          {formik.errors.processor_reason_code}
                        </FeildError>
                      )}
                  </FeildWraper>

                  {/* 24. QA Status */}
                  <FeildWraper width="33%" className={Style.responsive33}>
                    <FormLabel>QA Status</FormLabel>

                    <CustomSelect
                      name="qa_status"
                      options={qaStatusOptions}
                      formik={formik}
                      placeholder="Select QA Status"
                    />

                    {formik.touched.qa_status && formik.errors.qa_status && (
                      <FeildError>{formik.errors.qa_status}</FeildError>
                    )}
                  </FeildWraper>

                  {/* 25. Identified by QA */}
                  <FeildWraper width="33%" className={Style.responsive33}>
                    <FormLabel>Identified by QA</FormLabel>
                    <CustomSelect
                      name="identified_by_qa"
                      options={yesNoOptions}
                      formik={formik}
                      placeholder="Select Yes/No"
                    />
                    {formik.touched.identified_by_qa &&
                      formik.errors.identified_by_qa && (
                        <FeildError>
                          {formik.errors.identified_by_qa}
                        </FeildError>
                      )}
                  </FeildWraper>

                  {/* 26. Business Unit */}
                  <FeildWraper width="33%" className={Style.responsive33}>
                    <FormLabel>Business Unit</FormLabel>
                    <CustomSelect
                      name="business_unit"
                      options={agentOptions}
                      formik={formik}
                      placeholder="Select Business Unit"
                    />
                    {formik.touched.business_unit &&
                      formik.errors.business_unit && (
                        <FeildError>{formik.errors.business_unit}</FeildError>
                      )}
                  </FeildWraper>



                  {/* 28. QA Executive */}
                  <FeildWraper width="33%" className={Style.responsive33}>
                    <FormLabel>QA Executive</FormLabel>
                    <CustomSelect
                      name="qa_executive"
                      options={agentOptions}
                      formik={formik}
                      placeholder="Select QA Executive"
                    />
                    {formik.touched.qa_executive &&
                      formik.errors.qa_executive && (
                        <FeildError>{formik.errors.qa_executive}</FeildError>
                      )}
                  </FeildWraper>

                  {/* 33. QA Analysis */}
                  <FeildWraper width="33%" className={Style.responsive33}>
                    <FormLabel>QA Analysis</FormLabel>
                    <input
                      type="text"
                      name="qa_analysis"
                      value={formik.values.qa_analysis}
                      onChange={formik.handleChange}
                      placeholder="QA Analysis"
                    />
                    {formik.touched.qa_analysis &&
                      formik.errors.qa_analysis && (
                        <FeildError>{formik.errors.qa_analysis}</FeildError>
                      )}
                  </FeildWraper>

                  {/* 29. Responsible */}
                  <FeildWraper width="33%" className={Style.responsive33}>
                    <FormLabel>Responsible</FormLabel>
                    <CustomSelect
                      name="responsible"
                      options={responsibleOptions}
                      formik={formik}
                      placeholder="Select Responsible"
                    />
                    {formik.touched.responsible &&
                      formik.errors.responsible && (
                        <FeildError>{formik.errors.responsible}</FeildError>
                      )}
                  </FeildWraper>

                  {formik.values.responsible == "1" && (
                    <>
                      {/* 32. Responsible Person */}
                      <FeildWraper width="50%" className={Style.responsive33}>
                        <FormLabel>Responsible Person</FormLabel>
                        <CustomSelect
                          name="responsible_person"
                          options={agentOptions}
                          formik={formik}
                          placeholder="Select Responsible Person"
                        />
                        {formik.touched.responsible_person &&
                          formik.errors.responsible_person && (
                            <FeildError>
                              {formik.errors.responsible_person}
                            </FeildError>
                          )}
                      </FeildWraper>

                      {/* 30. Team Lead 1 */}
                      <FeildWraper width="50%" className={Style.responsive33}>
                        <FormLabel>Team Lead 1</FormLabel>
                        <CustomSelect
                          name="team_lead_1"
                          options={agentOptions}
                          formik={formik}
                          placeholder="Select Team Lead 1"
                        />
                        {formik.touched.team_lead_1 &&
                          formik.errors.team_lead_1 && (
                            <FeildError>{formik.errors.team_lead_1}</FeildError>
                          )}
                      </FeildWraper>

                      {/* 31. Team Lead 2 */}
                      <FeildWraper width="50%" className={Style.responsive33}>
                        <FormLabel>Team Lead 2</FormLabel>
                        <CustomSelect
                          name="team_lead_2"
                          options={agentOptions}
                          formik={formik}
                          placeholder="Select Team Lead 2"
                        />
                        {formik.touched.team_lead_2 &&
                          formik.errors.team_lead_2 && (
                            <FeildError>{formik.errors.team_lead_2}</FeildError>
                          )}
                      </FeildWraper>

                      {/* 27. Reporting Authority */}
                      <FeildWraper width="50%" className={Style.responsive33}>
                        <FormLabel>Reporting Authority</FormLabel>
                        <CustomSelect
                          name="reporting_authority"
                          options={agentOptions}
                          formik={formik}
                          placeholder="Select Reporting Authority"
                        />
                        {formik.touched.reporting_authority &&
                          formik.errors.reporting_authority && (
                            <FeildError>
                              {formik.errors.reporting_authority}
                            </FeildError>
                          )}
                      </FeildWraper>
                    </>
                  )}



                  {/* 34. Disagreement with QA */}
                  <FeildWraper width="33%" className={Style.responsive33}>
                    <FormLabel>Disagreement with QA</FormLabel>
                    <CustomSelect
                      name="disagreement_with_qa"
                      options={yesNoOptions}
                      formik={formik}
                      placeholder="Select Yes/No"
                    />
                    {formik.touched.disagreement_with_qa &&
                      formik.errors.disagreement_with_qa && (
                        <FeildError>
                          {formik.errors.disagreement_with_qa}
                        </FeildError>
                      )}
                  </FeildWraper>

                  {/* 35. QA Decision Changed */}
                  <FeildWraper width="33%" className={Style.responsive33}>
                    <FormLabel>QA Decision Changed</FormLabel>
                    <CustomSelect
                      name="qa_decision_changed"
                      options={yesNoOptions}
                      formik={formik}
                      placeholder="Select Yes/No"
                    />
                    {formik.touched.qa_decision_changed &&
                      formik.errors.qa_decision_changed && (
                        <FeildError>
                          {formik.errors.qa_decision_changed}
                        </FeildError>
                      )}
                  </FeildWraper>

                  {/* 36. QA of QA */}
                  <FeildWraper width="33%" className={Style.responsive33}>
                    <FormLabel>QA of QA</FormLabel>
                    <input
                      type="text"
                      name="qa_of_qa"
                      value={formik.values.qa_of_qa}
                      onChange={formik.handleChange}
                      placeholder="QA of QA"
                    />
                    {formik.touched.qa_of_qa && formik.errors.qa_of_qa && (
                      <FeildError>{formik.errors.qa_of_qa}</FeildError>
                    )}
                  </FeildWraper>

                  {/* 37. First Call Upsell Attempt */}
                  <FeildWraper width="33%" className={Style.responsive33}>
                    <FormLabel>First Call Upsell Attempt</FormLabel>
                    <CustomSelect
                      name="first_call_upsell_attempt"
                      options={yesNoOptions}
                      formik={formik}
                      placeholder="Select Yes/No"
                    />
                    {formik.touched.first_call_upsell_attempt &&
                      formik.errors.first_call_upsell_attempt && (
                        <FeildError>
                          {formik.errors.first_call_upsell_attempt}
                        </FeildError>
                      )}
                  </FeildWraper>

                  {/* 38. Repitch Attempt */}
                  <FeildWraper width="33%" className={Style.responsive33}>
                    <FormLabel>Repitch Attempt</FormLabel>
                    <CustomSelect
                      name="repitch_attempted"
                      options={yesNoOptions}
                      formik={formik}
                      placeholder="Select Yes/No"
                    />
                    {formik.touched.repitch_attempted &&
                      formik.errors.repitch_attempted && (
                        <FeildError>
                          {formik.errors.repitch_attempted}
                        </FeildError>
                      )}
                  </FeildWraper>

                  {/* 39. Repitch Outcome */}
                  <FeildWraper width="33%" className={Style.responsive33}>
                    <FormLabel>Repitch Outcome</FormLabel>
                    <CustomSelect
                      name="repitch_outcome"
                      options={repitchOutcomeOptions}
                      formik={formik}
                      placeholder="Select Repitch Outcome"
                    />
                    {formik.touched.repitch_outcome &&
                      formik.errors.repitch_outcome && (
                        <FeildError>{formik.errors.repitch_outcome}</FeildError>
                      )}
                  </FeildWraper>

                  {/* 40. CB Outcome */}
                  <FeildWraper width="33%" className={Style.responsive33}>
                    <FormLabel>CB Outcome</FormLabel>
                    <CustomSelect
                      name="cb_outcome"
                      options={cbOutcomeOptions}
                      formik={formik}
                      placeholder="Select CB Outcome"
                    />
                    {formik.touched.cb_outcome && formik.errors.cb_outcome && (
                      <FeildError>{formik.errors.cb_outcome}</FeildError>
                    )}
                  </FeildWraper>

                  {/* 42. Recovery Rate */}
                  <FeildWraper width="33%" className={Style.responsive33}>
                    <FormLabel>Recovery Rate (%)</FormLabel>
                    {/* <CustomSelect
                      name="recovery_rate_percent"
                      options={percentageOptions}
                      formik={formik}
                      placeholder="Select Recovery Rate"
                    /> */}
                    <input
                      type="text"
                      name="recovery_rate_percent"
                      value={formik.values.recovery_rate_percent}
                      onChange={formik.handleChange}
                      placeholder="Recovery Rate"
                    />

                    {formik.touched.recovery_rate_percent &&
                      formik.errors.recovery_rate_percent && (
                        <FeildError>
                          {formik.errors.recovery_rate_percent}
                        </FeildError>
                      )}
                  </FeildWraper>

                  {/* 43. Case Study */}
                  <FeildWraper width="33%" className={Style.responsive33}>
                    <FormLabel>Case Study</FormLabel>
                    <input
                      type="text"
                      name="case_study"
                      value={formik.values.case_study}
                      onChange={formik.handleChange}
                      placeholder="Case Study"
                    />
                    {formik.touched.case_study && formik.errors.case_study && (
                      <FeildError>{formik.errors.case_study}</FeildError>
                    )}
                  </FeildWraper>
                </FormArea>
              </div>
            </div>
          </div>
        </div>

        <div className={`${Style.dcmb__cardFoot}`}>
          <button
            type="reset"
            className={`${Style.dcmb__cancel}`}
            onClick={onClose}
          >
            Discard
          </button>
          <button type="submit" className={Style.dcmb__submit}>
            Apply
          </button>
        </div>
      </LeftSidePopupLayout>
    </form>
  );
};

export default RefundForm;
